import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Users as UsersIcon, Search, Plus, Power, Edit3, Check, X,
  Shield, Mail, UserCircle, Clock, KeyRound,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { fmtDate } from "@/lib/dates";

// =============================================================================
// AdminUsersScreen — /admin/users
// T-070 Phase A — director-only user administration.
//
// What this screen does (Phase A):
//   * Lists every user in the system with role, employee link, status,
//     last sign-in.
//   * Inline role change (director/manager/supervisor/field/subcontractor).
//   * Activate / deactivate toggle (soft delete — keeps audit trail).
//   * Search filter (name / email / role).
//
// What this screen does NOT do (parked for Phase B+):
//   * Invite new users — needs Worker endpoint with Supabase service-key.
//     For now, create users via Supabase dashboard or the existing
//     onboarding flow.
//   * Password reset — same Worker dependency.
//   * IT support ticket capture — Phase C, separate surface.
//
// RLS already enforces "manager+ can read/write users" (migration 013), so
// this screen relies on the standard supabase client.
// =============================================================================

const ROLES = ["director", "manager", "supervisor", "field", "subcontractor", "kiosk"] as const;
type Role = typeof ROLES[number];

const ROLE_TONE: Record<Role, "open" | "won" | "warning" | "default" | "notice"> = {
  director:      "open",       // accent — top of pyramid
  manager:       "won",        // green — leadership
  supervisor:    "notice",     // blue — site lead
  field:         "default",    // neutral — frontline
  subcontractor: "warning",    // amber — external
  kiosk:         "notice",     // blue — a crew tablet (mig 410), not a person
};

interface UserRow {
  user_id:        string;
  auth_user_id:   string | null;
  login_email:    string;
  role_name:      Role;
  payroll_access: boolean;
  is_active:      boolean;
  last_login_at:  string | null;
  invited_at:     string | null;
  activated_at:   string | null;
  login_type:     string;
  employee_id:    string | null;
  employee: {
    employee_id: string;
    first_name:  string;
    last_name:   string;
    email:       string | null;
    is_active:   boolean;
  } | null;
}

// -----------------------------------------------------------------------------

export default function AdminUsersScreen() {
  const me = useRequireAuth();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [showInvite, setShowInvite] = useState(false);

  // Director gate — duplicate of route protection so a manager who somehow
  // hits the URL directly gets the message rather than a half-broken page.
  const isDirector = hasRole(me, "director");

  const { data: users = [], isLoading } = useQuery({
    enabled: isDirector,
    queryKey: ["admin_users", showInactive],
    queryFn: async () => {
      let q = supabase
        .from("users")
        .select(`
          user_id, auth_user_id, login_email, role_name, payroll_access, is_active,
          last_login_at, invited_at, activated_at, login_type, employee_id,
          employee:employees(employee_id, first_name, last_name, email, is_active)
        `)
        .order("role_name", { ascending: true })
        .order("login_email", { ascending: true });
      if (!showInactive) q = q.eq("is_active", true);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as UserRow[];
    },
  });

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(u => {
      const empName = u.employee
        ? `${u.employee.first_name} ${u.employee.last_name}`.toLowerCase()
        : "";
      return (
        u.login_email.toLowerCase().includes(needle) ||
        empName.includes(needle) ||
        u.role_name.toLowerCase().includes(needle)
      );
    });
  }, [users, search]);

  // -- Mutations --------------------------------------------------------------
  const updateRoleMut = useMutation({
    mutationFn: async ({ user_id, role_name }: { user_id: string; role_name: Role }) => {
      const { error } = await supabase
        .from("users")
        .update({ role_name })
        .eq("user_id", user_id);
      if (error) throw error;
    },
    onSuccess: () => {
      setEditingRole(null);
      qc.invalidateQueries({ queryKey: ["admin_users"] });
    },
  });

  const toggleActiveMut = useMutation({
    mutationFn: async ({ user_id, is_active }: { user_id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("users")
        .update({ is_active })
        .eq("user_id", user_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin_users"] }),
  });

  // T-446 — grant/revoke payroll access (see bank/super to run pay) without
  // making the person a director. RLS on users allows manager+; this screen is
  // director-only anyway.
  const togglePayrollMut = useMutation({
    mutationFn: async ({ user_id, payroll_access }: { user_id: string; payroll_access: boolean }) => {
      const { error } = await supabase
        .from("users")
        .update({ payroll_access })
        .eq("user_id", user_id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin_users"] }),
    onError: (e: any) => alert(`Couldn't update payroll access: ${e?.message ?? e}`),
  });

  // T-134 — Director clicks "Reset password" on a user row → worker fires a
  // Supabase auth recovery email at the user's login address. They click the
  // link, set a new password, log back in. No more dropping into Supabase
  // dashboard for routine password ops.
  const resetPasswordMut = useMutation({
    mutationFn: async (userId: string) => {
      const res = await apiFetch(`/admin/users/${userId}/send-password-reset`, {
        method: "POST",
      });
      const payload = await res.json();
      if (!res.ok) {
        throw new Error(payload.detail ?? payload.error ?? `Reset failed (${res.status})`);
      }
      return payload as { email: string; message: string };
    },
    onSuccess: (result) => {
      alert(result.message ?? `Password reset email sent to ${result.email}.`);
    },
    onError: (err: any) => {
      alert(`Couldn't send password reset: ${err?.message ?? err}`);
    },
  });

  // -- Counts -----------------------------------------------------------------
  const counts = useMemo(() => {
    const c = { total: users.length, active: 0, byRole: {} as Record<string, number> };
    for (const u of users) {
      if (u.is_active) c.active++;
      c.byRole[u.role_name] = (c.byRole[u.role_name] ?? 0) + 1;
    }
    return c;
  }, [users]);

  if (!me) return null;

  if (!isDirector) {
    return (
      <div className="px-6 md:px-10 py-6 md:py-8">
        <Card className="max-w-xl mx-auto p-6">
          <EmptyState
            icon={Shield}
            title="Director access only"
            description="User administration is restricted to directors. Speak to a director if you need a role changed or a new user invited."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="px-6 md:px-10 py-6 md:py-8 pb-20">
      <Link
        to="/portal"
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14}/> Portal
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-[20px] font-semibold text-text flex items-center gap-2">
            <UsersIcon size={20} className="text-accent" />
            User Administration
          </h1>
          <div className="text-[12px] text-text-muted mt-0.5">
            Manage logins, roles, and access for your team.
            {" "}{counts.active} active · {counts.total} total.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 bg-ink-raised border border-line rounded-md w-72 max-w-[60vw]">
            <Search size={14} className="text-text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, role…"
              className="bg-transparent border-none outline-none text-text text-[13px] flex-1"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-text-muted hover:text-text" title="Clear">
                <X size={12} />
              </button>
            )}
          </div>
          <Link to="/admin/crew-tablets"><Button variant="secondary" title="Crew tablet logins — mig 410">Crew tablets</Button></Link>
          <Button
            icon={Plus}
            onClick={() => setShowInvite(true)}
            title="Send a Supabase magic-link invite + create the public.users row"
          >
            Invite user
          </Button>
        </div>
      </div>

      {/* Role count strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-4">
        {ROLES.map(r => (
          <div key={r} className="bg-ink-raised border border-line rounded-md p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">{r}</div>
            <div className="text-[18px] font-mono font-semibold text-text mt-1">{counts.byRole[r] ?? 0}</div>
          </div>
        ))}
      </div>

      {/* Show-inactive toggle */}
      <div className="flex items-center gap-3 mb-3">
        <label className="inline-flex items-center gap-2 text-[12px] text-text-muted cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="accent-accent"
          />
          Show inactive users
        </label>
      </div>

      {/* Table */}
      <Card>
        {isLoading ? (
          <div className="py-10 flex justify-center"><Spinner /></div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={UsersIcon}
            title="No users match"
            description={search
              ? `Nothing matches "${search}".`
              : "No users in this view yet."}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line">
                  {["User", "Login email", "Role", "Payroll", "Status", "Last sign-in", "Invited", ""].map(h => (
                    <th
                      key={h}
                      className="text-left px-4 py-2.5 text-[10px] font-medium uppercase tracking-wider2 text-text-muted"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, idx) => {
                  const isMe = u.user_id === me.user_id;
                  const isEditingThis = editingRole === u.user_id;
                  return (
                    <tr
                      key={u.user_id}
                      className={`border-b border-line last:border-b-0 ${idx % 2 ? "bg-ink/40" : ""} ${!u.is_active ? "opacity-60" : ""}`}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-accent/12 text-accent text-[11px] font-semibold flex items-center justify-center flex-shrink-0">
                            {(u.employee?.first_name?.[0] ?? u.login_email[0] ?? "?").toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-text font-medium truncate">
                              {u.employee
                                ? `${u.employee.first_name} ${u.employee.last_name}`
                                : <span className="italic text-text-muted">No employee linked</span>}
                              {isMe && <span className="ml-2 text-[10px] text-accent font-mono">(you)</span>}
                            </div>
                            <div className="text-[10px] font-mono text-text-dim mt-0.5">
                              {u.login_type}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-text-muted font-mono truncate max-w-[260px]" title={u.login_email}>
                        <Mail size={11} className="inline mr-1 text-text-dim"/>
                        {u.login_email}
                      </td>
                      <td className="px-4 py-2.5">
                        {isEditingThis ? (
                          <div className="inline-flex items-center gap-1">
                            <select
                              defaultValue={u.role_name}
                              onChange={(e) => {
                                const next = e.target.value as Role;
                                if (next === u.role_name) { setEditingRole(null); return; }
                                if (isMe && next !== "director") {
                                  if (!confirm("Demoting yourself from director will lock you out of this screen and other admin functions. Continue?")) return;
                                }
                                updateRoleMut.mutate({ user_id: u.user_id, role_name: next });
                              }}
                              className="bg-ink border border-line rounded px-2 py-1 text-[12px] text-text"
                              disabled={updateRoleMut.isPending}
                              autoFocus
                            >
                              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <button
                              onClick={() => setEditingRole(null)}
                              className="p-1 text-text-muted hover:text-text"
                              title="Cancel"
                            >
                              <X size={12}/>
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setEditingRole(u.user_id)}
                            className="inline-flex items-center gap-1.5 group"
                            title="Click to change role"
                          >
                            <Pill label={u.role_name} tone={ROLE_TONE[u.role_name]} small />
                            <Edit3 size={10} className="text-text-dim opacity-70"/>
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {u.role_name === "manager" ? (
                          <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] text-text-muted" title="Grants access to bank / super / payroll data + the payroll export — for a manager who runs payroll, without making them a director">
                            <input
                              type="checkbox"
                              className="accent-accent"
                              checked={!!u.payroll_access}
                              disabled={togglePayrollMut.isPending}
                              onChange={(e) => togglePayrollMut.mutate({ user_id: u.user_id, payroll_access: e.target.checked })}
                            />
                            {u.payroll_access ? "Yes" : "—"}
                          </label>
                        ) : u.role_name === "director" ? (
                          <span className="text-text-dim text-[11px]" title="Directors have payroll access by role">via director</span>
                        ) : (
                          <span className="text-text-dim text-[11px]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        <Pill
                          label={u.is_active ? "Active" : "Inactive"}
                          tone={u.is_active ? "ok" : "default"}
                          small
                        />
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-text-muted font-mono whitespace-nowrap">
                        {u.last_login_at ? (
                          <><Clock size={10} className="inline mr-1 text-text-dim"/>{fmtDate(u.last_login_at)}</>
                        ) : (
                          <span className="text-text-dim italic">Never</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-[12px] text-text-muted font-mono whitespace-nowrap">
                        {u.invited_at ? fmtDate(u.invited_at) : <span className="text-text-dim">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          {/* Reset password — director-only, only for active
                              users. Hidden when looking at your own row (use
                              "Forgot password" from the login screen). */}
                          {!isMe && u.is_active && (
                            <button
                              onClick={() => {
                                const name = u.employee ? `${u.employee.first_name} ${u.employee.last_name}` : u.login_email;
                                if (!confirm(`Send password reset email to ${name} (${u.login_email})?`)) return;
                                resetPasswordMut.mutate(u.user_id);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-text-muted hover:text-accent hover:bg-accent/10 transition-colors"
                              title="Send password reset email"
                              disabled={resetPasswordMut.isPending}
                            >
                              <KeyRound size={11}/>
                              Reset password
                            </button>
                          )}

                          {isMe ? (
                            <span className="text-[10px] text-text-dim italic">cannot deactivate self</span>
                          ) : (
                            <button
                              onClick={() => {
                                const next = !u.is_active;
                                const verb = next ? "Reactivate" : "Deactivate";
                                const name = u.employee ? `${u.employee.first_name} ${u.employee.last_name}` : u.login_email;
                                if (!confirm(`${verb} ${name}? ${next ? "They'll be able to log in again." : "They will be unable to log in until reactivated."}`)) return;
                                toggleActiveMut.mutate({ user_id: u.user_id, is_active: next });
                              }}
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors ${
                                u.is_active
                                  ? "text-text-muted hover:text-danger hover:bg-danger/10"
                                  : "text-text-muted hover:text-ok hover:bg-ok/10"
                              }`}
                              title={u.is_active ? "Deactivate user" : "Reactivate user"}
                              disabled={toggleActiveMut.isPending}
                            >
                              <Power size={11}/>
                              {u.is_active ? "Deactivate" : "Reactivate"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Footer note */}
      <div className="mt-4 text-[11px] text-text-dim leading-snug">
        <strong className="text-text-muted">Phase B live.</strong> Invite a user → they get a Supabase magic-link
        email → click the link → set their password → land in the app with the role you assigned. Password reset
        + IT ticket capture still to come.
      </div>

      {showInvite && (
        <InviteUserModal
          onClose={() => setShowInvite(false)}
          onSent={() => {
            setShowInvite(false);
            qc.invalidateQueries({ queryKey: ["admin_users"] });
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// InviteUserModal — T-070 Phase B
// Posts to the Worker /admin/users/invite endpoint which sends the auth
// magic-link email + creates the public.users row server-side.
// =============================================================================
function InviteUserModal({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const [email, setEmail] = useState("");
  const [roleName, setRoleName] = useState<Role>("field");
  const [loginType, setLoginType] = useState<"employee" | "subcontractor">("employee");
  const [employeeId, setEmployeeId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Pull employees so the inviter can optionally link the new login to one
  const { data: employees = [] } = useQuery({
    queryKey: ["invite_employees"],
    queryFn: async () => {
      const { data } = await supabase
        .from("employees")
        .select("employee_id, first_name, last_name, email")
        .eq("is_active", true)
        .order("last_name");
      return (data ?? []) as any[];
    },
  });

  async function handleInvite() {
    setErr(null);
    if (!email.trim()) { setErr("Email required."); return; }
    setBusy(true);
    try {
      const res = await apiFetch("/admin/users/invite", {
        method: "POST",
        body: JSON.stringify({
          email:        email.trim(),
          role_name:    roleName,
          employee_id:  employeeId || null,
          login_type:   loginType,
        }),
      });
      const payload = await res.json();
      if (!res.ok) {
        setErr(payload.detail ?? payload.error ?? `Invite failed (${res.status})`);
        return;
      }
      setDone(payload.message ?? "Invite sent.");
      // Auto-close after 2s
      setTimeout(() => onSent(), 2000);
    } catch (e: any) {
      setErr(e?.message ?? "Network error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={busy ? undefined : onClose}>
      <div className="bg-ink-card border border-line rounded-lg shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Mail size={16} className="text-accent"/>
            <div className="text-[15px] font-semibold text-text">Invite user</div>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 rounded hover:bg-ink-hover text-text-muted disabled:opacity-50">
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="py-6 text-center">
            <Check size={32} className="text-ok mx-auto mb-2"/>
            <div className="text-[14px] text-text font-medium">{done}</div>
            <div className="text-[11px] text-text-muted mt-1">Magic link is on its way.</div>
          </div>
        ) : (
          <>
            {err && (
              <div className="mb-3 px-3 py-2 bg-danger/12 border border-danger/30 rounded text-[12px] text-danger">{err}</div>
            )}

            <div className="mb-3">
              <label className="block text-[11px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="josh@coretelgroup.com"
                className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
                disabled={busy}
              />
            </div>

            <div className="mb-3 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Role *</label>
                <select value={roleName} onChange={(e) => setRoleName(e.target.value as Role)} disabled={busy} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-wider2 text-text-muted mb-1">Login type</label>
                <select
                  value={loginType}
                  onChange={(e) => {
                    const v = e.target.value as "employee" | "subcontractor";
                    setLoginType(v);
                    // T-154 — when flipping to subcontractor, default the
                    // role to 'subcontractor' (the lowest-priv post-invite
                    // shell). The subbie wizard later flips role to 'field'
                    // for Labour-track users. Director can still override.
                    if (v === "subcontractor") setRoleName("subcontractor" as Role);
                    else if (roleName === "subcontractor") setRoleName("field" as Role);
                  }}
                  disabled={busy}
                  className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
                >
                  <option value="employee">employee</option>
                  <option value="subcontractor">subcontractor</option>
                </select>
              </div>
            </div>

            {/* T-154 — Subbie hint. Surfaces when login_type=subcontractor so
                the inviter knows what the user will see + reminds about the
                personal-email pattern. Closes #17. */}
            {loginType === "subcontractor" && (
              <div className="mb-3 px-3 py-2 bg-accent/8 border border-accent/30 rounded text-[11px] text-text leading-relaxed">
                <div className="font-medium text-accent mb-0.5">Subcontractor invite</div>
                On first login this user will see the subbie wizard and pick
                Labour or Supplier themselves. Use their <em>personal</em> email
                (e.g. gmail) — sole-trader subbies rarely have a company
                domain, and they'll keep this address if they leave.
              </div>
            )}

            <div className="mb-3">
              <label className="block text-[11px] font-mono uppercase tracking-wider2 text-text-muted mb-1">
                Link to employee (optional)
              </label>
              <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} disabled={busy} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
                <option value="">— don't link —</option>
                {employees.map((e: any) => (
                  <option key={e.employee_id} value={e.employee_id}>
                    {e.first_name} {e.last_name}{e.email ? ` (${e.email})` : ""}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-text-dim mt-1">
                Link the login to an existing employee record so timesheets, leave, etc. tie back to the right person.
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-line">
              <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={handleInvite} disabled={busy} icon={Mail}>
                {busy ? "Sending…" : "Send invite"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
